<template>
  <div class="country_selector">
    <button class="country_selector__selected-button" @click="toggleOpen">
      <img
        v-if="selectedCountry"
        class="country_selector__flag"
        :src="flagUrlForCode(selectedCountry.code)"
        :alt="`Flag of ${selectedCountry.name}`" />
    </button>
    <ul v-if="selectionOpen" class="country_selector__options-list">
      <li v-if="showCountryTaxOptions" class="country_selector__search-option">
        <b>Price Display</b>
        <show-tax-selector />
      </li>
      <li class="country_selector__search-option">
        <input
          class="country_selector__search-input"
          type="text"
          v-model="searchTerm"
          placeholder="Search for your Country" />
      </li>
      <li v-for="option in filteredList" :key="option.code">
        <button
          class="country_selector__option-button"
          :class="selectOptionClass(option)"
          @click="changeCountry(option)">
          <img
            loading="lazy"
            class="country_selector__flag"
            :src="flagUrlForCode(option.code)"
            :alt="`Flag of ${option.name}`" />
          <span v-html="option.name" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { fetchActiveCountryData } from "~/hooks/queries/useCountriesQuery";
import type { CountryQueryResponse } from "~/hooks/queries/useCountriesQuery";
import { startListening, stopListening } from "~/common/ts/clickAway";
import { useUserStore } from "~/stores/user";
import { useCartStore } from "~/stores/cart";

export default {
  props: {
    countryOptions: Array as CountryQueryResponse[],
  },
  setup() {
    const userStore = useUserStore()
    const cartStore = useCartStore()

    return { cartStore, userStore }
  },
  created() {
    this.fetchActiveCountry();
  },
  data () {
    return {
      selectionOpen: false,
      searchTerm: '',
      selectedCountry: null as CountryQueryResponse|null,
    };
  },
  computed: {
    showCountryTaxOptions () {
      // screen width must be below 1024
      return this.userStore.getCountryHasTax && window.innerWidth < 1024
    },
    filteredList () {
      const normalized = this.searchTerm.toLowerCase();

      return this.searchTerm
        ? this.countryOptions.filter((option) => option.code.toLowerCase().includes(normalized) || option.name.toLowerCase().includes(normalized))
        : this.countryOptions;
    },
    userSelectedCountry () {
      return this.userStore.getSelectedCountry
    }
  },
  methods: {
    async fetchActiveCountry () {
      const data = await fetchActiveCountryData();

      if (data?.activeCountry) {
        this.selectedCountry = data.activeCountry;

        this.userStore.setShowEURegulationConfirmation(this.selectedCountry?.isEuVerifyRequired ?? false)

        this.userStore.setCountryHasTax(this.selectedCountry?.isEuCountry ?? false)
      }
    },
    toggleOpen () {
      if (this.selectionOpen) {
        this.closeOverlay();
      } else {
        this.selectionOpen = true;
        startListening(this.$el, this.closeOverlay);
      }
    },
    closeOverlay () {
      this.selectionOpen = false;
      stopListening(this.$el, this.closeOverlay);
    },
    async changeCountry (selectedCountry: CountryQueryResponse) {
      this.selectedCountry = selectedCountry;
      this.closeOverlay();

      await this.userStore.changeUserSelectedCountry(this.selectedCountry.code)
    },
    selectOptionClass (option: CountryQueryResponse) {
      return {
        'country_selector__option-button--active': option.code === this.selectedCountry.code,
      };
    },
    flagUrlForCode (countryCode: string): string {
      return `/assets/flagpedia-net/${countryCode.toLowerCase()}.svg`;
    },
  },
  watch: {
    userSelectedCountry(newVal) {
      if (newVal !== this.selectedCountry?.code) {
        this.selectedCountry = {
          code: newVal,
          name: '',
          currencyCode: this.userStore.getSelectedCurrency
        }

        this.changeCountry(this.selectedCountry)
      }
    },
  },
};
</script>

<style lang="scss">
@use '~/common/scss/helpers';
@use '~/common/scss/mq';
@use '~/common/scss/variables';

.country_selector {
  $root: &;

  position: relative;

  &__option-button,
  &__selected-button {
    align-items: center;
    display: flex;
  }

  &__selected-button {
    align-items: center;
    border: 1px solid #FFF;
    display: flex;
    justify-content: center;
    object-fit: cover;
    overflow: hidden;
    padding: .25rem .25rem;

    #{$root}__flag {
      aspect-ratio: 40 / 28;
      border-radius: 3px;
      height: 1.25rem;
      width: auto;
    }
  }

  &__option-button {
    background-color: #FFF;
    color: #000;
    gap: .5rem;
    height: auto;
    line-height: 1.3rem;
    padding: .25rem .5rem;
    text-align: left;
    transition: background-color .25s, color .25s;
    width: 100%;

    &--active,
    &:hover {
      background-color: #F0F0F0;
      color: #111111;
    }

    #{$root}__flag {
      border: 1px solid #FFF;
      border-radius: 3px;
      flex: 0 0 helpers.px-to-rem(40);
      height: helpers.px-to-rem(28);
      overflow: hidden;
      width: helpers.px-to-rem(40);
    }
  }

  &__flag {
    height: 100%;
    object-fit: cover;
    width: 100%;
    margin-left: .35rem;
  }

  &__options-list {
    background-color: #FFF;
    border-radius: 3px;
    box-shadow: 0 .25rem .5rem #333;
    max-height: max(50vh, #{helpers.px-to-rem(450)});
    overflow: auto;
    position: absolute;
    right: 0;
    top: helpers.px-to-rem(40);
    width: helpers.px-to-rem(250);
  }

  &__search-option {
    padding: .25rem .5rem;
  }

  &__search-input {
    background-color: #FFF;
    border: 1px solid #000;
    border-radius: 3px;
    color: #000;
    font-family: inherit;
    height: helpers.px-to-rem(30);
    padding: .25rem .5rem;
    width: 100%;

    &:focus {
      border-color: var(--color-orange);
      outline: none;
    }
  }

  @include mq.mobileOnly {
    &__selected-button {
      background: none;
      border: .1rem solid variables.$colorBlack;
      border-radius: 50%;
      margin: .2rem -.5rem 0 .1rem;
      padding: 0;
      width: 1.1rem;
      height: 1.1rem;

      #{$root}__flag {
        border-radius: 50%;
        object-fit: cover;
        width: 20px;
        height: 20px;
      }
    }

    &__flag {
      margin-left: .0rem;
    }
  }
}
</style>
